import { useState, useCallback } from 'react';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import irWorkstationTheme from 'features/IRWorkstation/styles/IRWorkstationTheme';
import Navbar from 'features/IRWorkstation/components/Navbar';
import IntegrationStrip from 'features/IRWorkstation/components/IntegrationStrip';
import SalesforceTaskDrawer from 'features/IRWorkstation/components/SalesforceTaskDrawer';
import AlertsDrawer from 'features/IRWorkstation/components/AlertsDrawer';
import NewsFeedDrawer from 'features/IRWorkstation/components/NewsFeedDrawer';
import LPPanel from 'features/IRWorkstation/components/LPPanel';
import ExplainerPanel from 'features/IRWorkstation/components/ExplainerPanel';
import Overview from 'features/IRWorkstation/pages/Overview';
import FundDashboard from 'features/IRWorkstation/pages/FundDashboard';
import CoInvestDashboard from 'features/IRWorkstation/pages/CoInvestDashboard';

const visitablePages = {
	overview: 'Overview',
	fund_dashboard: 'Fund Dashboard',
	co_invest_dashboard: 'Co-Invest Dashboard',
	reporting: 'Reporting',
	analytics: 'Analytics',
};

const IRWorkstation = () => {
	const [currentPage, setCurrentPage] = useState('overview');
	const [showFundVDetail, setShowFundVDetail] = useState(false);

	// lp panel state
	const [lpPanelOpen, setLpPanelOpen] = useState(false);
	const [selectedLp, setSelectedLp] = useState(null);

	// explainer state
	const [explainerOpen, setExplainerOpen] = useState(false);

	// salesforce task drawer state
	const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState(null);

	const handleOpenTaskDrawer = useCallback((task) => {
		setSelectedTask(task);
		setTaskDrawerOpen(true);
	}, []);

	const handleCloseTaskDrawer = useCallback(() => {
		setTaskDrawerOpen(false);
		setSelectedTask(null);
	}, []);

	// alerts drawer state and handlers
	const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false);
	const [alert, setAlert] = useState({});

	const handleOpenAlertsDrawer = useCallback((alert) => {
		setAlert(alert);
		setAlertsDrawerOpen(true);
	}, []);

	const handleCloseAlertsDrawer = useCallback(() => {
		setAlertsDrawerOpen(false);
	}, []);

	// news feed drawer state and handlers
	const [newsFeedDrawerOpen, setNewsFeedDrawerOpen] = useState(false);
	const [newsFeed, setNewsFeed] = useState({});

	const handleOpenNewsFeedDrawer = useCallback((newsFeed) => {
		setNewsFeed(newsFeed);
		setNewsFeedDrawerOpen(true);
	}, []);

	const handleCloseNewsFeedDrawer = useCallback(() => {
		setNewsFeedDrawerOpen(false);
	}, []);

	// lp panel handlers
	const handleOpenLP = useCallback((lp) => {
		setSelectedLp(lp);
		setLpPanelOpen(true);
	}, []);

	const handleCloseLP = useCallback(() => {
		setLpPanelOpen(false);
	}, []);

	const handleOpenFundV = useCallback(() => {
		setCurrentPage('fund_dashboard');
		setShowFundVDetail(true);
	}, []);

	const handleSetPage = useCallback((page) => {
		setCurrentPage(page);
		setShowFundVDetail(false);
	}, []);

	const handleBackToOverview = useCallback(() => {
		setCurrentPage('overview');
		setShowFundVDetail(false);
	}, []);

	// resolve which page to render
	const renderPage = () => {
		switch (currentPage) {
			case 'fund_dashboard':
				return (
					<FundDashboard
						showFundVDetail={showFundVDetail}
						setShowFundVDetail={setShowFundVDetail}
						onBack={handleBackToOverview}
					/>
				);
			case 'co_invest_dashboard':
				return <CoInvestDashboard onBack={handleBackToOverview} />;
			default:
				return (
					<Overview
						onOpenTaskDrawer={handleOpenTaskDrawer}
						onOpenAlertsDrawer={handleOpenAlertsDrawer}
						onOpenNewsFeedDrawer={handleOpenNewsFeedDrawer}
						onOpenFundV={handleOpenFundV}
					/>
				);
		}
	};

	return (
		<ThemeProvider theme={irWorkstationTheme}>
			<CssBaseline />
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100vh',
					width: '100vw',
					backgroundColor: 'background.default',
				}}
			>
				<Navbar
					currentPage={currentPage}
					setCurrentPage={handleSetPage}
					visitablePages={visitablePages}
					onOpenLP={handleOpenLP}
					onOpenExplainer={() => setExplainerOpen(true)}
				/>
				<IntegrationStrip />

				{/* page area — position:relative scopes the drawer backdrop to this box */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						minHeight: 0,
						position: 'relative',
						overflow: 'hidden',
					}}
				>
					{renderPage()}

					<SalesforceTaskDrawer
						open={taskDrawerOpen}
						task={selectedTask}
						onClose={handleCloseTaskDrawer}
					/>

					<AlertsDrawer
						alert={alert}
						open={alertsDrawerOpen}
						onClose={handleCloseAlertsDrawer}
					/>

					<NewsFeedDrawer
						newsFeed={newsFeed}
						open={newsFeedDrawerOpen}
						onClose={handleCloseNewsFeedDrawer}
					/>

					<LPPanel open={lpPanelOpen} lp={selectedLp} onClose={handleCloseLP} />
				</Box>
				<ExplainerPanel
					open={explainerOpen}
					onClose={() => setExplainerOpen(false)}
				/>
			</Box>
		</ThemeProvider>
	);
};

export default IRWorkstation;
